/**
 * Dashboard formatters + aggregators. Ports the legacy
 * `wf-audit/src/slices/dashboard/utils/*` helpers verbatim (no
 * behavioural change — same number / byte formatting the operator
 * has been seeing for months).
 */

export function formatNumber(n: number): string {
  if (!Number.isFinite(n)) return '—';
  return new Intl.NumberFormat('en-US').format(Math.round(n));
}

const BYTE_UNITS = ['B', 'KB', 'MB', 'GB', 'TB'];

export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const i = Math.min(BYTE_UNITS.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  const value = bytes / Math.pow(1024, i);
  return `${value.toFixed(value >= 100 ? 0 : 1)} ${BYTE_UNITS[i]}`;
}

export function formatDurationSeconds(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return '0s';
  const days = Math.floor(seconds / 86400);
  let remaining = seconds % 86400;
  const hours = Math.floor(remaining / 3600);
  remaining %= 3600;
  const minutes = Math.floor(remaining / 60);
  const secs = remaining % 60;
  const parts: string[] = [];
  if (days) parts.push(`${days}d`);
  if (hours) parts.push(`${hours}h`);
  if (minutes) parts.push(`${minutes}m`);
  if (secs || parts.length === 0) parts.push(`${Math.round(secs)}s`);
  return parts.join(' ');
}

export interface DashboardAggregates {
  totalScenarios: number;
  activeScenarios: number;
  inactiveScenarios: number;
  totalOperations: number;
  totalModules: number;
  totalDurationSeconds: number;
  scenariosWithKpis: number;
  scenariosLastRunWithin24h: number;
}

/**
 * Roll the loose-typed list-response rows into a single aggregate
 * record. Defensive about field absence — engine list response is
 * stable, but every field gets a numeric fallback so missing data
 * shows up as zero rather than `NaN`.
 */
export function aggregate(rows: ReadonlyArray<Record<string, unknown>>): DashboardAggregates {
  let activeScenarios = 0;
  let totalOperations = 0;
  let totalModules = 0;
  let totalDurationSeconds = 0;
  let scenariosWithKpis = 0;
  let scenariosLastRunWithin24h = 0;
  const dayCutoff = Date.now() - 24 * 60 * 60 * 1000;

  for (const row of rows) {
    if (row.status === 'On') activeScenarios += 1;
    totalOperations += toNumber(row.lastSuccessfulRunOperations);
    totalModules += toNumber(row.totalModules);
    totalDurationSeconds += toNumber(row.lastSuccessfulRunDuration);
    if (toNumber(row.kpiCount) > 0) scenariosWithKpis += 1;
    if (typeof row.lastSuccessfulRunAt === 'string') {
      const t = Date.parse(row.lastSuccessfulRunAt);
      if (Number.isFinite(t) && t >= dayCutoff) scenariosLastRunWithin24h += 1;
    }
  }
  return {
    totalScenarios: rows.length,
    activeScenarios,
    inactiveScenarios: rows.length - activeScenarios,
    totalOperations,
    totalModules,
    totalDurationSeconds,
    scenariosWithKpis,
    scenariosLastRunWithin24h,
  };
}

function toNumber(v: unknown): number {
  if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
  if (typeof v === 'string') {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
}

export interface TopScenario {
  fusionId: string;
  name: string;
  folderName: string;
  operations: number;
  modules: number;
  status: string;
  lastRunAt: string | null;
}

/**
 * Top-N scenarios by `lastSuccessfulRunOperations`. Mirrors the legacy
 * `ScenariosByValue` table — sorted DESC, ties broken by module count,
 * defaults to top-10.
 */
export function topScenariosByOperations(
  rows: ReadonlyArray<Record<string, unknown>>,
  limit = 10,
): ReadonlyArray<TopScenario> {
  return [...rows]
    .map((r) => ({
      fusionId: typeof r.fusionId === 'string' ? r.fusionId : '',
      name: typeof r.name === 'string' ? r.name : '',
      folderName: typeof r.folderName === 'string' ? r.folderName : '',
      operations: toNumber(r.lastSuccessfulRunOperations),
      modules: toNumber(r.totalModules),
      status: typeof r.status === 'string' ? r.status : '',
      lastRunAt: typeof r.lastSuccessfulRunAt === 'string' ? r.lastSuccessfulRunAt : null,
    }))
    .filter((r) => r.fusionId !== '' && r.name !== '')
    .sort((a, b) => b.operations - a.operations || b.modules - a.modules)
    .slice(0, limit);
}
